import { Component, Input, input, output, signal, computed, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { LTPrimeNgModule } from 'src/app/third-party/lt-primeng.module';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatExpansionModule } from '@angular/material/expansion';
import { ExportOptions } from 'src/app/features/grid/services/papa-parse-export-service.service';
import { GridSettingsFacade } from 'src/app/features/grid/classes/grid-settings.facade';
import { ExportManagerFacade, ExportStats, ExportResult } from '../../classes/export-manager.facade';
import { Subscription } from 'rxjs';
import { LtExportControlsComponent } from '../lt-export-controls/lt-export-controls.component';
import { LtExportConfigComponent } from '../lt-export-config/lt-export-config.component';
import { LtExportPreviewComponent } from '../lt-export-preview/lt-export-preview.component';

export interface ExportManagerConfig {
  showPreview?: boolean;
  showAdvancedOptions?: boolean;
  allowFilterSaving?: boolean;
  maxPreviewRows?: number;
  defaultFormat?: 'csv' | 'excel' | 'json';
  enableBulkExport?: boolean;
}

@Component({
  selector: 'app-lt-export-manager',
  imports: [
    CommonModule,
    FormsModule,
    LTPrimeNgModule,
    MatIconModule,
    MatButtonModule,
    MatCardModule,
    MatExpansionModule,
    LtExportControlsComponent,
    LtExportPreviewComponent,
    LtExportConfigComponent,
  ],
  standalone: true,
  templateUrl: './lt-export-manager.component.html',
  styleUrl: './lt-export-manager.component.scss',
  providers: [ExportManagerFacade],
})
export class LTExportManagerComponent implements OnInit, OnDestroy {
  private subscriptions = new Subscription();

  //#region Inputs
  gridFacade? = input<GridSettingsFacade>();

  readonly config = input<ExportManagerConfig>({
    showPreview: true,
    showAdvancedOptions: true,
    allowFilterSaving: true,
    maxPreviewRows: 5,
    defaultFormat: 'csv',
    enableBulkExport: true,
  });

  //#region Dialog Visibility
  private _isVisible = signal<boolean>(false);

  @Input() set visible(value: boolean) {
    this._isVisible.set(value);
    if (value) {
      // Reset state when dialog opens
      this.resetDialogState();
    }
  }
  
  isVisible(): boolean {
    return this._isVisible();
  }

  toggleVisibility() {
    const newValue = !this._isVisible();
    this._isVisible.set(newValue);
    this.visibleChange.emit(newValue);
    
    if (!newValue) {
      // Clean up when closing
      this.resetDialogState();
    }
  }

  private resetDialogState(): void {
    // Reset any temporary state when dialog opens/closes
    if (this._isVisible()) {
      // Refresh export stats when opening
      this.refreshExportStats();
    }
  }
  //#endregion Dialog Visibility

  //#endregion Inputs

  //#region Outputs
  readonly visibleChange = output<boolean>();
  readonly exportCompleted = output<{ format: string; recordCount: number; fileName: string }>();
  readonly exportCancelled = output<void>();
  //#endregion Outputs

  //#region State Signals
  readonly showPreview = signal<boolean>(false);
  readonly showAdvancedConfig = signal<boolean>(false);
  readonly showInstructions = signal<boolean>(false);
  readonly selectedExportScope = signal<'all' | 'filtered' | 'selected'>('all');
  
  readonly exportOptions = signal<ExportOptions>({
    includeAggregates: false,
    aggregatePosition: 'bottom',
    aggregateSeparatorRow: true,
    nestedColumnSeparator: ' - ',
    flattenNestedHeaders: true,
    customAggregateLabels: {},
  });

  // Export stats from facade
  readonly exportStats = signal<ExportStats>({
    totalRecords: 0,
    filteredRecords: 0,
    selectedRecords: 0,
    visibleColumns: 0,
    hasActiveFilters: false,
    hasAggregates: false,
    canExportAll: false,
    canExportFiltered: false,
    canExportSelected: false,
  });

  readonly isExporting = computed(() => this.exportManagerFacade.isExporting$());

  // Computed helpers
  readonly canExportAny = computed(() => {
    const stats = this.exportStats();
    return stats.canExportAll || stats.canExportFiltered || stats.canExportSelected;
  });
  //#endregion State Signals
  
  constructor(private readonly exportManagerFacade: ExportManagerFacade) {}

  ngOnInit(): void {
    // Initialize the export manager facade with grid facade
    if (this.gridFacade()) {
      this.exportManagerFacade.initialize(this.gridFacade());

      this.subscriptions.add(
        this.exportManagerFacade.getExportStats().subscribe(stats => {
          this.exportStats.set(stats);
          
          // Auto-select best export scope based on available data
          this.autoSelectExportScope(stats);
        }),
      );

      // Set default options based on context
      this.initializeExportOptions();

      
    }

    // Set display preferences from config
    const config = this.config();
    this.showPreview.set(config.showPreview ?? true);
    this.showAdvancedConfig.set(config.showAdvancedOptions ?? false);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  //#region Initialization Helpers

  private initializeExportOptions(): void {
    const defaultOptions = this.exportManagerFacade.createDefaultOptions('all');
    this.exportOptions.set(defaultOptions);
  }

  private autoSelectExportScope(stats: ExportStats): void {
    // Auto-select the most relevant export scope
    if (stats.selectedRecords > 0) {
      this.selectedExportScope.set('selected');
    } else if (stats.hasActiveFilters && stats.filteredRecords > 0) {
      this.selectedExportScope.set('filtered');
    } else {
      this.selectedExportScope.set('all');
    }
  }

  private refreshExportStats(): void {
    if (!this.gridFacade()) return;
    
    // Trigger a fresh stats calculation
    this.exportManagerFacade.getExportStats().subscribe(stats => {
      this.exportStats.set(stats);
    });
  }

  //#endregion Initialization Helpers

  //#region Event Handlers
  onOptionsChange(options: Partial<ExportOptions>): void {
    this.exportOptions.update(current => ({ ...current, ...options }));
  }

  onScopeChange(scope: 'all' | 'filtered' | 'selected'): void {
    this.selectedExportScope.set(scope);
    
    // Update export options labels based on scope
    this.updateExportLabelsForScope(scope);
  }

  private updateExportLabelsForScope(scope: 'all' | 'filtered' | 'selected'): void {
    const defaultLabels = this.exportManagerFacade['getDefaultLabels'](scope);
    this.exportOptions.update(current => ({
      ...current,
      customAggregateLabels: {
        ...current.customAggregateLabels,
        ...defaultLabels,
      },
    }));
  }

  // onExportRequested(exportType: 'all' | 'filtered' | 'selected'): void {
  //   this.executeExport(exportType);
  // }

  async onExportRequested(exportType: 'all' | 'filtered' | 'selected'): Promise<void> {
    this.selectedExportScope.set(exportType);
    await this.executeExport(exportType);
  }

  //#endregion Event Handlers

  //#region Actions
  togglePreview(): void {
    this.showPreview.update(value => !value);
  }

  toggleAdvancedConfig(): void {
    this.showAdvancedConfig.update(value => !value);
  }

  canExport(): boolean {
    const stats = this.exportStats();
    const scope = this.selectedExportScope();
    
    switch (scope) {
      case 'selected':
        return stats.canExportSelected;
      case 'filtered':
        return stats.canExportFiltered;
      case 'all':
      default:
        return stats.canExportAll;
    }
  }

  async executeExport(exportType: 'all' | 'filtered' | 'selected' = 'all'): Promise<void> {
    if (!this.canExport() || this.isExporting()) return;

    const scope = exportType || this.selectedExportScope();
    const options = this.exportOptions();
    let result: ExportResult;

    try {
      // Generate filename with scope indicator
      const fileName = this.exportManagerFacade.getRecommendedFileName(scope);

      switch (exportType) {
        case 'selected':
          result = await this.exportManagerFacade.exportSelectedPromise(options, fileName);
          break;
        case 'filtered':
          result = await this.exportManagerFacade.exportFilteredPromise(options, fileName);
          break;
        case 'all':
        default:
          result = await this.exportManagerFacade.exportAllPromise(options, fileName);
          break;
      }

      if (result.success) {
        this.exportCompleted.emit(result);
        this.toggleVisibility();
      } else {
        console.error('Export failed:', result.error);
        this.handleExportError(result.error || 'Unknown error occurred');
      }
    } catch (error) {
      console.error('Export error:', error);
      this.handleExportError(error.message || 'An unexpected error occurred');
    }
  }

  // executeExport(exportType: 'all' | 'filtered' | 'selected' = 'all'): void {
  //   if (!this.facade || !this.canExport()) return;

  //   const options = this.exportOptions();
  //   const context = this.exportContext();
    
  //   // Generate filename with timestamp
  //   const timestamp = new Date().toISOString().split('T')[0];
  //   const baseFileName = `export_${exportType}_${timestamp}`;
  //   const fileName = `${baseFileName}.csv`;

  //   try {
  //     // Determine export method based on type
  //     switch (exportType) {
  //       case 'selected':
  //         if (context.selectedRecords > 0) {
  //           this.facade.exportSelectedToCSVUpdated(fileName, options.includeAggregates);
  //           this.exportCompleted.emit({
  //             format: 'csv',
  //             recordCount: context.selectedRecords,
  //             fileName,
  //           });
  //         }
  //         break;
        
  //       case 'filtered':
  //         if (context.hasActiveFilters) {
  //           this.facade.exportToCSVUpdated(fileName, false, options.includeAggregates);
  //           this.exportCompleted.emit({
  //             format: 'csv',
  //             recordCount: context.filteredRecords,
  //             fileName,
  //           });
  //         }
  //         break;
        
  //       case 'all':
  //       default:
  //         this.facade.exportToCSVUpdated(fileName, false, options.includeAggregates);
  //         this.exportCompleted.emit({
  //           format: 'csv',
  //           recordCount: context.totalRecords,
  //           fileName,
  //         });
  //         break;
  //     }

  //     // Close dialog on successful export
  //     this.toggleVisibility();
      
  //   } catch (error) {
  //     console.error('Export failed:', error);
  //     // Handle error - could emit error event or show toast
  //   }
  // }

  cancel(): void {
    this.exportCancelled.emit();
    this.toggleVisibility();
  }

  private handleExportError(errorMessage: string): void {
    // You can emit an error event or show a toast notification here
    console.error('Export error:', errorMessage);
    
    // TODO - import toastService and display message
    // Optional: Show error in UI
    // this.messageService.add({
    //   severity: 'error',
    //   summary: 'Export Failed',
    //   detail: errorMessage,
    //   life: 7000
    // });
  }

  //#endregion Actions

  //#region Helper Methods

  /**
   * Get user-friendly description of current export scope
   */
  getExportScopeDescription(): string {
    const stats = this.exportStats();
    const scope = this.selectedExportScope();
    
    switch (scope) {
      case 'selected':
        return `Exporting ${stats.selectedRecords} selected record${stats.selectedRecords !== 1 ? 's' : ''}`;
      case 'filtered':
        return `Exporting ${stats.filteredRecords} filtered record${stats.filteredRecords !== 1 ? 's' : ''}`;
      case 'all':
      default:
        return `Exporting all ${stats.totalRecords} record${stats.totalRecords !== 1 ? 's' : ''}`;
    }
  }

  /**
   * Check if current scope is valid for export
   */
  isCurrentScopeValid(): boolean {
    const stats = this.exportStats();
    const scope = this.selectedExportScope();
    
    switch (scope) {
      case 'selected':
        return stats.selectedRecords > 0;
      case 'filtered':
        return stats.filteredRecords > 0 && stats.hasActiveFilters;
      case 'all':
      default:
        return stats.totalRecords > 0;
    }
  }

  //#endregion Helper Methods
}
